import { Bullets, Callout, Contact, LegalHeader, Mail, P, Section, Table } from "@/components/legal";
import { useSeoMeta } from "@/lib/useSeoMeta";

// Privacy Policy for SkillPatch, operated by LatentForce Intelligence Private Limited.
// Adapted from latentforce.ai/legal/privacy for SkillPatch (a registry for agentic skills):
// LatentGraph/codebase-graph, billing, and unused LLM-provider references have been removed.

const EFFECTIVE = "01/07/2026";

export default function PrivacyPage() {
  useSeoMeta({ title: "Privacy Policy — SkillPatch", path: "/legal/privacy" });

  return (
    <>
      <div className="grid-backdrop" aria-hidden />
    <main className="max-w-6xl px-6 py-12 sm:pl-16 sm:pr-10 lg:pl-28">
      <LegalHeader title="Privacy" accent="Policy" effective={EFFECTIVE}>
        <strong className="text-text/80">LatentForce Intelligence Private Limited</strong> ("LatentForce", "we", "us")
        operates SkillPatch, a registry for publishing, discovering, and improving agentic skills. This Privacy Policy
        explains what data we collect, how we use it, and your rights.
      </LegalHeader>

      <Section number="1" title="Scope">
        <P>This policy covers personal data we process when you:</P>
        <Bullets
          items={[
            "Visit the SkillPatch website",
            "Create or use a SkillPatch account",
            "Publish, install, rate, or suggest changes to skills",
            "Use our CLI, registry API, or API keys",
            "Interact with our support or feedback channels",
          ]}
        />
        <P>It does not cover the practices of third-party websites that link to or from us.</P>
      </Section>

      <Section number="2" title="Data We Collect">
        <Table
          headers={["Category", "Examples", "Source"]}
          rows={[
            [<strong>Account data</strong>, "display name, email, password hash, roles", "You"],
            [<strong>Connected accounts</strong>, "GitHub login and access token, used only when you link a skill to a GitHub repository", "You, via GitHub"],
            [<strong>Registry data</strong>, "skill names, versions, descriptions, tags, changelogs, ratings, bookmarks, team names and membership", "You"],
            [<strong>Official skill attribution</strong>, "public repository URL and author GitHub username for official skills we import", "Public GitHub repositories"],
            [<strong>Customer Content</strong>, "the skills you publish (SKILL.md and supporting files), suggested changes, and archives you upload", "You"],
            [<strong>Usage data</strong>, "features used, API calls, error logs, performance metrics", "Automatic"],
            [<strong>Device data</strong>, "IP address, browser/CLI version, OS", "Automatic"],
            [<strong>Communications</strong>, "support requests and feedback", "You"],
          ]}
        />
        <P>We do not knowingly collect personal data from children under 16.</P>
      </Section>

      <Section number="3" title="How We Use Data">
        <P>We process the data above to:</P>
        <Bullets
          items={[
            "Operate the registry — publish, version, install, search, rate, and suggest changes to skills",
            "Review skills and suggested changes for structure and security before they go live",
            "Sync skills you link to a GitHub repository",
            "Authenticate users and prevent fraud or abuse",
            "Provide support",
            "Improve product reliability and performance (aggregate metrics only)",
            "Send service announcements and, with your consent, product updates",
            "Comply with legal obligations",
          ]}
        />
        <P>
          Our legal bases under GDPR are: (a) performance of the contract with you, (b) our legitimate interests in
          operating and securing the Service, (c) your consent where required (e.g., marketing emails), and (d)
          compliance with legal obligations.
        </P>
      </Section>

      <Section number="4" title="AI Models and Training">
        <P>
          SkillPatch uses AI agents to review every skill and every suggested change, so we are explicit about how that
          works:
        </P>
        <Callout>
          We do not use Customer Content to train, fine-tune, or evaluate any AI models, and we do not permit our LLM
          provider to do so.
        </Callout>
        <Bullets
          items={[
            <>
              AI review — structure scanning, security review, and authoring suggested edits — runs through{" "}
              <strong>AWS Bedrock</strong> (Anthropic Claude). Where supported by the provider, we configure it for{" "}
              <strong>zero retention</strong> of prompts and completions.
            </>,
            "We may use aggregated, fully anonymised metrics (e.g., install counts) to improve the product. We never use these aggregates to reconstruct or expose any individual customer's content.",
          ]}
        />
        <P>
          If we ever change this posture, we will notify you by email <strong>at least 30 days before</strong> the change
          takes effect, and your prior content will not be retroactively used.
        </P>
      </Section>

      <Section number="5" title="Subprocessors">
        <P>
          We rely on a limited set of vetted subprocessors to operate the Service — currently{" "}
          <strong>Amazon Web Services</strong> for cloud infrastructure and hosting, and <strong>AWS Bedrock</strong> for
          the LLM inference used to review skills. We engage subprocessors only under written agreements that require them
          to protect your data.
        </P>
        <P>
          We give at least 30 days' notice before adding a new subprocessor that processes Customer Content.
        </P>
      </Section>

      <Section number="6" title="Sharing">
        <P>We do not sell personal data, and we do not engage in cross-context behavioural advertising.</P>
        <P>We share data only with:</P>
        <Bullets
          items={[
            <>
              <strong>Subprocessors</strong> acting on our behalf under written agreements (see the Subprocessors section)
            </>,
            <>
              <strong>Authorities</strong>, when required by valid legal process — we will challenge overbroad requests
              and notify you where lawful to do so
            </>,
            <>
              <strong>Successors</strong>, in the event of a merger, acquisition, or asset sale; the same protections
              continue to apply
            </>,
          ]}
        />
        <P>
          Skills, versions, and ratings you choose to <strong>publish</strong> are public by design and can be viewed and
          installed by anyone. This policy governs personal data, not content you deliberately make public.
        </P>
      </Section>

      <Section title="Official Skills">
        <P>
          Some skills in the registry are <strong>official skills</strong> that we import from publicly available GitHub
          repositories published under a permissive open-source license. For these, we process the original author's
          public GitHub username and repository URL to provide attribution and link back to the source, based on our
          legitimate interest in operating an attributed registry.
        </P>
        <P>
          If you are the author, you can verify via GitHub to claim, consent to, or request removal of the listing at any
          time — no SkillPatch account required. To request removal, you can also contact us at <Mail />.
        </P>
      </Section>

      <Section number="7" title="Data Location and Transfers">
        <P>SkillPatch runs on Amazon Web Services (AWS).</P>
        <P>
          Where data is transferred out of the EEA, UK, or Switzerland, we rely on{" "}
          <strong>Standard Contractual Clauses</strong> (and the UK IDTA where applicable) and, where appropriate,
          additional safeguards such as encryption and access controls.
        </P>
      </Section>

      <Section number="8" title="Retention">
        <Table
          headers={["Data", "Retention"]}
          rows={[
            ["Account data", "Life of account + 90 days after deletion"],
            ["Customer Content (skills, versions, suggestions)", "Life of account; deleted within 30 days after account closure"],
            ["Usage logs", "13 months"],
            ["Support requests", "24 months"],
          ]}
        />
        <P>Backups are retained for an additional 35 days and then deleted on the next rotation.</P>
      </Section>

      <Section number="9" title="Security">
        <P>
          We maintain a written security program, including: TLS in transit, encryption at rest, role-based access
          controls, audit logging, vendor reviews, and a defined incident response process. GitHub access tokens are
          stored encrypted. We will notify affected users of a confirmed personal data breach without undue delay and in
          accordance with applicable law.
        </P>
      </Section>

      <Section number="10" title="Your Rights (GDPR, UK GDPR)">
        <P>
          If you are in the EEA, UK, or Switzerland, you have the right to: access, rectify, erase, restrict processing,
          object, and port your personal data, and to withdraw consent at any time. To exercise any of these, email{" "}
          <Mail />. We will respond within 30 days. You also have the right to complain to your supervisory authority.
        </P>
      </Section>

      <Section number="11" title="Your Rights (India — DPDP Act, 2023)">
        <P>
          If you are in India, you have the right to: access a summary of personal data we process, request correction,
          completion or update, request erasure (subject to legal retention), nominate another person to exercise your
          rights in case of death or incapacity, and grievance redressal. You may withdraw consent at any time where
          processing is based on consent. We act as a <strong>Data Fiduciary</strong> under the DPDP Act, 2023 for
          personal data we determine the purposes and means of processing.
        </P>
        <P>
          To exercise these rights or raise a grievance, contact our <strong>Grievance Officer</strong> at <Mail />. We
          will acknowledge within 48 hours and resolve within the timelines required by the DPDP Act and any rules issued
          thereunder. If the matter is not resolved to your satisfaction, you may approach the{" "}
          <strong>Data Protection Board of India</strong>.
        </P>
      </Section>

      <Section number="12" title="Your Rights (US State Laws)">
        <P>
          If you are a resident of California, Colorado, Connecticut, Virginia, Utah, or another US state with a
          comprehensive privacy law, you have the right to know what we collect, request deletion or correction, opt out
          of "sale" (we do not sell) and targeted advertising (we do not engage in cross-context behavioural
          advertising), and to non-discrimination for exercising your rights. To exercise these rights, email <Mail />.
        </P>
      </Section>

      <Section number="13" title="Cookies and Tracking">
        <P>
          We use cookies and similar browser storage only for essential purposes — keeping you signed in and securing
          your session. We do not use advertising or cross-site tracking cookies. You can clear this storage through your
          browser at any time.
        </P>
      </Section>

      <Section number="14" title="Changes">
        <P>
          We may update this Privacy Policy from time to time. For material changes, we will notify active customers by
          email at least 30 days before the change takes effect, and update the "Effective" date above.
        </P>
      </Section>

      <Section number="15" title="Contact">
        <Contact
          lines={[
            <>
              <strong>LatentForce Intelligence Private Limited</strong> (CIN: U62011KA2025PTC205213)
            </>,
            "A company incorporated under the Companies Act, 2013.",
            "Registered office: 3rd Floor, Sree Gururaya Mansion, 8th Main, JP Nagar III Phase, Bangalore South, Bangalore - 560078, Karnataka",
            <>Privacy / Data Protection: <Mail /></>,
            <>
              <strong>Grievance Officer (DPDP Act, 2023):</strong> <Mail />
            </>,
          ]}
        />
        <P>
          If you are in the EU and require an EU representative under Article 27 of the GDPR, please contact <Mail /> and
          we will direct you to our appointed representative.
        </P>
      </Section>
    </main>
    </>
  );
}
